import React, { useState } from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import type { AiCodeProgressEvent } from '~/services/PromoVoucherAiService';

type Props = {
  events: Array<AiCodeProgressEvent>;
  includeSave?: boolean;
  validationPassed?: boolean;
};

const steps: Array<{ step: AiCodeProgressEvent['step']; label: string }> = [
  { step: 'identity', label: 'Check code identity' },
  { step: 'metadata', label: 'Load promo metadata' },
  { step: 'generation', label: 'Generate AI proposal' },
  { step: 'payload', label: 'Check proposed fields' },
  { step: 'validation', label: 'Validate proposal' },
  { step: 'write', label: 'Save changes' },
  { step: 'readback', label: 'Read saved record' },
  { step: 'check', label: 'Check saved code' },
];

export default function AiWorkflowProgress({ events, includeSave = false, validationPassed }: Props) {
  const [expandedSteps, setExpandedSteps] = useState<Array<AiCodeProgressEvent['step']>>([]);
  const latestByStep = new Map(events.map((event) => [event.step, event]));

  return (
    <Paper variant="outlined" sx={{ p: 2 }} aria-live="polite">
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        AI tool steps
      </Typography>
      <Stack spacing={1}>
        {steps.slice(0, includeSave ? steps.length : 5).map(({ step, label }) => {
          const event = latestByStep.get(step);
          const history = events.filter((entry) => entry.step === step);
          const isSkipped = event?.status === 'skipped';
          const isDone = event?.status === 'done' && (step !== 'validation' || validationPassed !== false);
          const isRunning = event?.status === 'running';
          const isFailed = event?.status === 'failed' || (step === 'validation' && event?.status === 'done' && !isDone);
          return (
            <Box key={step}>
              <Accordion
                disableGutters
                elevation={0}
                expanded={expandedSteps.includes(step)}
                onChange={(_, expanded) =>
                  setExpandedSteps((current) =>
                    expanded ? [...current, step] : current.filter((entry) => entry !== step),
                  )
                }
                sx={{ '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} disabled={!event}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {isRunning ? (
                      <CircularProgress size={18} aria-label={`${label} in progress`} />
                    ) : isDone ? (
                      <CheckCircleOutlineIcon color="success" fontSize="small" />
                    ) : (
                      <RadioButtonUncheckedIcon color={isFailed ? 'error' : 'disabled'} fontSize="small" />
                    )}
                    <Chip
                      size="small"
                      label={
                        isRunning
                          ? 'Running'
                          : isFailed
                            ? 'Needs attention'
                            : isDone
                              ? 'Completed'
                              : isSkipped
                                ? 'Skipped'
                                : 'Waiting'
                      }
                      color={isFailed ? 'error' : isRunning ? 'info' : isDone ? 'success' : 'default'}
                      variant="outlined"
                    />
                    <Box>
                      <Typography variant="body2" color={isFailed ? 'error' : 'text.primary'}>
                        {isSkipped
                          ? 'Step skipped'
                          : isFailed && step === 'validation'
                            ? 'Validation needs attention'
                            : label}
                      </Typography>
                      {event && (
                        <Typography variant="caption" color="text.secondary">
                          {event.detail}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {history.map((entry, index) => (
                      <Box key={index}>
                        <Typography variant="body2" color={entry.status === 'failed' ? 'error' : 'text.secondary'}>
                          {entry.attempt ? `Attempt ${entry.attempt} · ` : ''}
                          {entry.status}: {entry.detail}
                        </Typography>
                        {entry !== event && (
                          <Stack spacing={0.5}>
                            {entry.errors?.map((error, errorIndex) => (
                              <Alert key={`error-${errorIndex}`} severity="error">
                                {error}
                              </Alert>
                            ))}
                            {entry.warnings?.map((warning, warningIndex) => (
                              <Alert key={`warning-${warningIndex}`} severity="warning">
                                {warning}
                              </Alert>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
              {event && !!(event.errors?.length || event.warnings?.length) && (
                <Stack spacing={0.5} sx={{ px: 2, pb: 1 }}>
                  {event.attempt && <Typography variant="caption">Validation attempt {event.attempt}</Typography>}
                  {event.errors?.map((error, errorIndex) => (
                    <Alert key={`error-${errorIndex}`} severity="error">
                      {error}
                    </Alert>
                  ))}
                  {event.warnings?.map((warning, warningIndex) => (
                    <Alert key={`warning-${warningIndex}`} severity="warning">
                      {warning}
                    </Alert>
                  ))}
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}
